import React from 'react';
import {
  Compass,
  Flame,
  Camera,
  Landmark,
  Mountain,
  Building2,
  Moon,
  Sparkles,
  Salad,
  UtensilsCrossed,
  Target,
  ShoppingBag,
  Zap,
  CalendarDays,
  Dumbbell,
} from 'lucide-react';
import { SpotCategory, Language } from '../types/spot';

interface CategoryFilterProps {
  selectedCategory: SpotCategory;
  onSelectCategory: (cat: SpotCategory) => void;
  language: Language;
}

export const CategoryFilter: React.FC<CategoryFilterProps> = ({
  selectedCategory,
  onSelectCategory,
  language,
}) => {
  const categories: {
    id: SpotCategory;
    labelDe: string;
    shortDe: string;
    labelEn: string;
    shortEn: string;
    labelKr: string;
    shortKr: string;
    icon: React.ElementType;
  }[] = [
    {
      id: 'all',
      labelDe: 'Alle Highlights',
      shortDe: 'Alle',
      labelEn: 'All Highlights',
      shortEn: 'All',
      labelKr: '전체 명소',
      shortKr: '전체',
      icon: Compass,
    },
    {
      id: 'extras',
      labelDe: '🔥 Coole Extras (13)',
      shortDe: 'Extras (13)',
      labelEn: '🔥 Cool Extras (13)',
      shortEn: 'Extras (13)',
      labelKr: '🔥 이색 엑스트라 (13선)',
      shortKr: '엑스트라 (13)',
      icon: Flame,
    },
    {
      id: 'photo_spot',
      labelDe: 'Fotospots',
      shortDe: 'Fotospots',
      labelEn: 'Photo Spots',
      shortEn: 'Photo',
      labelKr: '포토스팟',
      shortKr: '포토',
      icon: Camera,
    },
    {
      id: 'palace_heritage',
      labelDe: 'Paläste & Hanok',
      shortDe: 'Paläste',
      labelEn: 'Palaces & Hanok',
      shortEn: 'Palaces',
      labelKr: '궁궐 & 한옥',
      shortKr: '궁궐',
      icon: Landmark,
    },
    {
      id: 'cultural_hidden',
      labelDe: 'Gassen & Cafés',
      shortDe: 'Cafés',
      labelEn: 'Alleys & Cafes',
      shortEn: 'Cafes',
      labelKr: '골목 & 카페',
      shortKr: '카페',
      icon: Sparkles,
    },
    {
      id: 'nature_viewpoint',
      labelDe: 'Natur & Aussicht',
      shortDe: 'Natur',
      labelEn: 'Nature & Views',
      shortEn: 'Nature',
      labelKr: '자연 & 전망',
      shortKr: '자연',
      icon: Mountain,
    },
    {
      id: 'shopping',
      labelDe: 'Shopping & Malls',
      shortDe: 'Shopping',
      labelEn: 'Shopping & Malls',
      shortEn: 'Shopping',
      labelKr: '쇼핑 & 몰',
      shortKr: '쇼핑',
      icon: ShoppingBag,
    },
    {
      id: 'event_festival',
      labelDe: 'Events & Festivals',
      shortDe: 'Events',
      labelEn: 'Events & Festivals',
      shortEn: 'Events',
      labelKr: '축제 & 행사',
      shortKr: '축제',
      icon: CalendarDays,
    },
    {
      id: 'night_view',
      labelDe: 'Nachtansichten',
      shortDe: 'Nacht',
      labelEn: 'Night Views',
      shortEn: 'Night',
      labelKr: '야경 명소',
      shortKr: '야경',
      icon: Moon,
    },
    {
      id: 'modern_architecture',
      labelDe: 'Architektur',
      shortDe: 'Architektur',
      labelEn: 'Architecture',
      shortEn: 'Architektur',
      labelKr: '현대 건축',
      shortKr: '건축',
      icon: Building2,
    },
    {
      id: 'activity',
      labelDe: 'Aktivitäten',
      shortDe: 'Aktivitäten',
      labelEn: 'Activities',
      shortEn: 'Activities',
      labelKr: '액티비티',
      shortKr: '액티비티',
      icon: Target,
    },
    {
      id: 'fitness',
      labelDe: 'Fitness & Gyms',
      shortDe: 'Fitness',
      labelEn: 'Fitness & Gyms',
      shortEn: 'Fitness',
      labelKr: '피트니스 & 야외헬스',
      shortKr: '헬스',
      icon: Dumbbell,
    },
    {
      id: 'vegetarian_food',
      labelDe: 'Veggie & Vegan',
      shortDe: 'Veggie',
      labelEn: 'Vegetarian & Vegan',
      shortEn: 'Veggie',
      labelKr: '채식 & 비건',
      shortKr: '비건',
      icon: Salad,
    },
    {
      id: 'halal_food',
      labelDe: 'Halal & Universell',
      shortDe: 'Halal',
      labelEn: 'Halal Food',
      shortEn: 'Halal',
      labelKr: '할랄 식당',
      shortKr: '할랄',
      icon: UtensilsCrossed,
    },
    {
      id: 'charging_spot',
      labelDe: 'Kostenlos laden',
      shortDe: 'Laden',
      labelEn: 'Free Charging',
      shortEn: 'Charging',
      labelKr: '무료 충전소',
      shortKr: '충전',
      icon: Zap,
    },
  ];

  return (
    <div className="category-scroll" role="tablist" aria-label="Kategorie Filter">
      {categories.map((cat) => {
        const Icon = cat.icon;
        const isSelected = selectedCategory === cat.id;
        const label =
          language === 'de'
            ? cat.labelDe
            : language === 'kr'
            ? cat.labelKr
            : cat.labelEn;
        const shortLabel =
          language === 'de'
            ? cat.shortDe
            : language === 'kr'
            ? cat.shortKr
            : cat.shortEn;

        return (
          <button
            key={cat.id}
            type="button"
            role="tab"
            aria-selected={isSelected}
            onClick={() => onSelectCategory(cat.id)}
            className={`chip ${isSelected ? 'chip--active' : ''}`}
            title={label}
          >
            <Icon className={`chip__icon ${isSelected ? 'chip__icon--active' : 'chip__icon--muted'}`} />
            <span className="leading-none chip__label-full">{label}</span>
            <span className="leading-none chip__label-short">{shortLabel}</span>
          </button>
        );
      })}
    </div>
  );
};
